import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { IntervalsActivity, ActivityMatch } from '@/types';

interface ActivityState {
    // Data
    activities: Record<string, IntervalsActivity>; // Keyed by activity ID
    matches: Record<string, ActivityMatch>;        // Keyed by workout_instance_id (Stable ID)

    // Sync State
    isLoading: boolean;
    lastSyncedAt: string | null;

    // Actions
    setActivities: (activities: IntervalsActivity[]) => void;
    addActivity: (activity: IntervalsActivity) => void;
    setMatches: (matches: ActivityMatch[]) => void;
    updateMatch: (instanceId: string, match: ActivityMatch) => void;
    removeMatch: (instanceId: string) => void;
    setLoading: (loading: boolean) => void;
    setLastSyncedAt: (date: string) => void;

    // Selectors/Helpers
    getMatchForWorkout: (instanceId: string) => ActivityMatch | undefined;
    getActivity: (activityId: string) => IntervalsActivity | undefined;
    reset: () => void;
}

export const useActivityStore = create<ActivityState>()(
    immer((set, get) => ({
        activities: {},
        matches: {},
        isLoading: false,
        lastSyncedAt: null,

        setActivities: (newActivities) => set((state) => {
            newActivities.forEach(a => {
                state.activities[a.id] = a;
            });
        }),

        addActivity: (activity) => set((state) => {
            state.activities[activity.id] = activity;
        }),

        setMatches: (newMatches) => set((state) => {
            // Re-index by instanceId
            newMatches.forEach(m => {
                state.matches[m.workout_instance_id] = m;
            });
        }),

        updateMatch: (instanceId, match) => set((state) => {
            state.matches[instanceId] = match;
        }),

        removeMatch: (instanceId) => set((state) => {
            delete state.matches[instanceId];
        }),

        setLoading: (loading) => set((state) => {
            state.isLoading = loading;
        }),

        setLastSyncedAt: (date) => set((state) => {
            state.lastSyncedAt = date;
        }),

        getMatchForWorkout: (instanceId) => get().matches[instanceId],
        getActivity: (activityId) => get().activities[activityId],

        reset: () => set((state) => {
            state.activities = {};
            state.matches = {};
            state.isLoading = false;
            state.lastSyncedAt = null;
        })
    }))
);
